import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  BarChart3,
  Package,
  ClipboardList,
  ShieldCheck,
  Users,
  ChevronDown,
  ChevronRight,
  ChevronsDownUp,
  ChevronsUpDown,
  Search,
  X,
  Copy,
} from 'lucide-react';
import type { Team, Permission, SubPermission, TeamPermission } from '@/types/teams';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import { AssetUnsavedChangesModal } from '@/components/assets/asset-unsaved-changes-modal';
import { ClonePermissionsModal } from '../ClonePermissionsModal';
import { SubPermissionCard } from './SubPermissionCard';

// Helper function to find a sub-permission by ID - extracted to reduce nesting depth (S2004)
function findSubPermissionById(sourcePermission: TeamPermission | undefined, subPermId: string): SubPermission | undefined {
  return sourcePermission?.subPermissions?.find(sp => sp.id === subPermId);
}

// Helper function to check if a sub-permission is enabled - extracted to reduce nesting depth (S2004)
function isSubPermissionEnabled(
  permissionState: Record<string, { enabled: boolean; subPermissions: Record<string, boolean> }>,
  permissionId: string,
  subPermId: string
): boolean {
  return permissionState[permissionId]?.subPermissions[subPermId] || false;
}

// Helper function to get card border class based on status - extracted to fix nested ternary (S3358)
function getCardBorderClass(status: string): string {
  if (status === 'enabled') {
    return 'border-green-200 bg-green-50/30';
  }
  if (status === 'partial') {
    return 'border-amber-200 bg-amber-50/30';
  }
  return '';
}

// Helper function to get icon container class based on status - extracted to fix nested ternary (S3358)
function getIconContainerClass(status: string): string {
  if (status === 'enabled') {
    return 'bg-green-100 text-green-700';
  }
  if (status === 'partial') {
    return 'bg-amber-100 text-amber-700';
  }
  return 'bg-muted text-muted-foreground';
}

interface TeamPermissionsTabProps {
  readonly team: Team;
  readonly teams?: Team[];
  readonly permissions: Permission[];
  readonly isEditMode: boolean;
  readonly onPermissionsChange?: (grantedPermissions: string[]) => void;
  readonly onHasChanges?: (hasChanges: boolean) => void;
  readonly onSave?: () => void;
  readonly onCancel?: () => void;
}

// Map category icons to Lucide components
const categoryIcons: Record<string, any> = {
  'bar-chart-3': BarChart3,
  'package': Package,
  'clipboard-list': ClipboardList,
  'shield-check': ShieldCheck,
  'users-cog': Users,
};

// Map screen keys to their menu icons
const screenIcons: Record<string, any> = {
  'dashboard': BarChart3,
  'assets': Package,
  'tasks': ClipboardList,
  'compliance': ShieldCheck,
  'administration': Users,
};

type PermissionStatus = 'enabled' | 'partial' | 'disabled';

interface PermissionState {
  [permissionId: string]: {
    enabled: boolean;
    subPermissions: {
      [subPermId: string]: boolean;
    };
  };
}

export function TeamPermissionsTab({
  team,
  teams = [],
  permissions,
  isEditMode,
  onPermissionsChange,
  onHasChanges,
  onSave: onSaveCallback,
  onCancel: onCancelCallback,
}: Readonly<TeamPermissionsTabProps>) {
  // Safely handle permissions - use empty array if not valid
  const safePermissions = permissions && Array.isArray(permissions) ? permissions : [];
  const hasPermissions = safePermissions.length > 0;

  // Group permissions by category
  const groupedPermissions = safePermissions.reduce((acc, permission) => {
    const category = permission.category ?? 'Other';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(permission);
    return acc;
  }, {} as Record<string, Permission[]>);

  // Helper function to build sub-permission state for a single permission
  const buildSubPermissionState = (
    permission: Permission,
    teamPermission: Team['permissions'][number] | undefined
  ): { [key: string]: boolean } => {
    const subPermState: { [key: string]: boolean } = {};
    if (!permission.subPermissions) return subPermState;
    
    for (const subPerm of permission.subPermissions) {
      const teamSubPerm = teamPermission?.subPermissions?.find(tsp => tsp.key === subPerm.key);
      subPermState[subPerm.id] = teamSubPerm?.granted || false;
    }
    return subPermState;
  };

  // Initialize permission state from team's granted permissions
  const initializePermissionState = (): PermissionState => {
    const state: PermissionState = {};

    for (const permission of safePermissions) {
      const teamPermission = team.permissions?.find(tp => tp.permissionId === permission.id);
      const subPermState = buildSubPermissionState(permission, teamPermission);

      // For permissions without sub-permissions, check if the permission itself is granted
      let isEnabled = false;
      if (!permission.subPermissions || permission.subPermissions.length === 0) {
        isEnabled = teamPermission?.granted || false;
      } else {
        // For permissions with sub-permissions, enabled if any sub-permission is granted
        isEnabled = Object.values(subPermState).some(v => v);
      }

      state[permission.id] = {
        enabled: isEnabled,
        subPermissions: subPermState,
      };
    }

    return state;
  };

  const [permissionState, setPermissionState] = useState<PermissionState>(
    initializePermissionState()
  );
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCloneModal, setShowCloneModal] = useState(false);

  // Initialize all sections as open
  useEffect(() => {
    const initialOpenState: Record<string, boolean> = {};
    Object.keys(groupedPermissions).forEach((category) => {
      initialOpenState[category] = true;
    });
    setOpenSections(initialOpenState);
  }, []);

  // Notify parent when hasChanges state changes
  useEffect(() => {
    onHasChanges?.(hasChanges);
  }, [hasChanges, onHasChanges]);

  // Sync permission changes to parent whenever permissionState changes
  // This ensures the parent always has the current list of granted permissions
  // so they can be saved when the user clicks "Save Changes" in the header
  useEffect(() => {
    if (!onPermissionsChange) return;
    
    const grantedPermissions: string[] = [];
    Object.entries(permissionState).forEach(([permId, state]) => {
      const permission = safePermissions.find(p => p.id === permId);
      if (permission && (!permission.subPermissions || permission.subPermissions.length === 0)) {
        if (state.enabled) {
          grantedPermissions.push(permId);
        }
      } else {
        Object.entries(state.subPermissions).forEach(([subPermId, enabled]) => {
          if (enabled) {
            grantedPermissions.push(subPermId);
          }
        });
      }
    });
    
    onPermissionsChange(grantedPermissions);
  }, [permissionState, safePermissions, onPermissionsChange]);

  // Safety check for permissions - render empty state after hooks
  if (!hasPermissions) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">
        No permissions data available
      </div>
    );
  }

  // Calculate permission status
  const getPermissionStatus = (permission: Permission): PermissionStatus => {
    const state = permissionState[permission.id];
    if (!state) return 'disabled';

    // For permissions without sub-permissions, check the enabled flag directly
    if (!permission.subPermissions || permission.subPermissions.length === 0) {
      return state.enabled ? 'enabled' : 'disabled';
    }

    const enabledCount = Object.values(state.subPermissions).filter(Boolean).length;
    const totalCount = permission.subPermissions.length;

    if (enabledCount === 0) return 'disabled';
    if (enabledCount === totalCount) return 'enabled';
    return 'partial';
  };

  // Get enabled sub-permission count
  const getEnabledCount = (permission: Permission): string => {
    if (!permission.subPermissions || permission.subPermissions.length === 0) {
      return '0/0';
    }

    const state = permissionState[permission.id];
    if (!state) return `0/${permission.subPermissions.length}`;

    const enabledCount = Object.values(state.subPermissions).filter(Boolean).length;
    return `${enabledCount}/${permission.subPermissions.length}`;
  };

  // Toggle section open/closed
  const toggleSection = (category: string) => {
    setOpenSections((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  // Collapse all sections
  const collapseAll = () => {
    const allClosed: Record<string, boolean> = {};
    Object.keys(groupedPermissions).forEach((category) => {
      allClosed[category] = false;
    });
    setOpenSections(allClosed);
  };

  // Expand all sections
  const expandAll = () => {
    const allOpen: Record<string, boolean> = {};
    Object.keys(groupedPermissions).forEach((category) => {
      allOpen[category] = true;
    });
    setOpenSections(allOpen);
  };

  // Clone permissions from another team
  const handleClonePermissions = (sourceTeamId: string) => {
    const sourceTeam = teams.find(t => t.id === sourceTeamId);
    if (!sourceTeam) return;

    // Create new permission state based on source team
    const newState: PermissionState = {};

    permissions.forEach((permission) => {
      const sourcePermission = sourceTeam.permissions?.find(p => p.id === permission.id);

      newState[permission.id] = {
        enabled: false,
        subPermissions: {},
      };

      permission.subPermissions?.forEach((subPerm) => {
        const sourceSubPerm = findSubPermissionById(sourcePermission, subPerm.id);
        newState[permission.id].subPermissions[subPerm.id] = sourceSubPerm?.granted || false;
      });

      // Update enabled status based on sub-permissions
      const enabledCount = Object.values(newState[permission.id].subPermissions).filter(Boolean).length;
      newState[permission.id].enabled = enabledCount > 0;
    });

    setPermissionState(newState);
    setHasChanges(true);
    setShowCloneModal(false);
  };

  // Filter permissions based on search query
  const filterPermissions = (permission: Permission): boolean => {
    if (!searchQuery.trim()) return true;

    const query = searchQuery.toLowerCase();

    // Check if permission name matches
    if (permission.name?.toLowerCase().includes(query)) {
      return true;
    }

    // Check if permission description matches
    if (permission.description?.toLowerCase().includes(query)) {
      return true;
    }

    // Check if any sub-permission matches
    if (permission.subPermissions?.some(subPerm =>
      subPerm.name?.toLowerCase().includes(query) ||
      subPerm.description?.toLowerCase().includes(query)
    )) {
      return true;
    }

    return false;
  };

  // Get filtered grouped permissions
  const filteredGroupedPermissions = Object.entries(groupedPermissions).reduce((acc, [category, perms]) => {
    const filteredPerms = perms.filter(filterPermissions);
    if (filteredPerms.length > 0) {
      acc[category] = filteredPerms;
    }
    return acc;
  }, {} as Record<string, Permission[]>);

  // Clear search
  const clearSearch = () => {
    setSearchQuery('');
  };

  // Toggle main permission (enables/disables all sub-permissions)
  const toggleMainPermission = (permission: Permission) => {
    if (!isEditMode) return;

    const currentStatus = getPermissionStatus(permission);
    const newState = currentStatus !== 'enabled';

    setPermissionState((prev) => {
      const newSubPermissions: { [key: string]: boolean } = {};
      
      // If permission has sub-permissions, toggle them all
      if (permission.subPermissions && permission.subPermissions.length > 0) {
        permission.subPermissions.forEach((subPerm) => {
          newSubPermissions[subPerm.id] = newState;
        });
      }

      return {
        ...prev,
        [permission.id]: {
          enabled: newState,
          subPermissions: newSubPermissions,
        },
      };
    });

    setHasChanges(true);
  };

  // Toggle individual sub-permission
  const toggleSubPermission = (permissionId: string, subPermId: string) => {
    if (!isEditMode) return;

    setPermissionState((prev) => {
      const currentValue = prev[permissionId]?.subPermissions[subPermId] || false;

      return {
        ...prev,
        [permissionId]: {
          ...prev[permissionId],
          subPermissions: {
            ...prev[permissionId].subPermissions,
            [subPermId]: !currentValue,
          },
        },
      };
    });

    setHasChanges(true);
  };

  const handleConfirmSave= () => {
    const grantedPermissions: string[] = [];

    Object.entries(permissionState).forEach(([permId, state]) => {
      // Find the permission definition
      const permission = safePermissions.find(p => p.id === permId);
      
      // If permission has no sub-permissions and is enabled, add the permission ID itself
      if (permission && (!permission.subPermissions || permission.subPermissions.length === 0)) {
        if (state.enabled) {
          grantedPermissions.push(permId);
        }
      } else {
        // For permissions with sub-permissions, add enabled sub-permission IDs
        Object.entries(state.subPermissions).forEach(([subPermId, enabled]) => {
          if (enabled) {
            grantedPermissions.push(subPermId);
          }
        });
      }
    });

    onPermissionsChange?.(grantedPermissions);
    setHasChanges(false);
    setShowSaveConfirmation(false);
    onSaveCallback?.();

    // TODO: Call API to save permissions
    console.log('Saving permissions:', grantedPermissions);
  };

  const handleConfirmLeave= () => {
    setPermissionState(initializePermissionState());
    setHasChanges(false);
    setShowUnsavedChangesModal(false);
    onCancelCallback?.();
  };

  // Get status badge
  const getStatusBadge = (status: PermissionStatus) => {
    switch (status) {
      case 'enabled':
        return (
          <Badge variant="default" className="bg-green-500 hover:bg-green-600">
            Enabled
          </Badge>
        );
      case 'partial':
        return (
          <Badge variant="default" className="bg-amber-500 hover:bg-amber-600">
            Partially Enabled
          </Badge>
        );
      case 'disabled':
        return (
          <Badge variant="secondary">
            Disabled
          </Badge>
        );
    }
  };

  // Select all permissions
  const selectAll = () => {
    if (!isEditMode) return;

    const newState: PermissionState = {};

    safePermissions.forEach((permission) => {
      const subPermissions: { [key: string]: boolean } = {};
      
      // If permission has sub-permissions, enable them all
      if (permission.subPermissions && permission.subPermissions.length > 0) {
        permission.subPermissions.forEach((subPerm) => {
          subPermissions[subPerm.id] = true;
        });
      }

      newState[permission.id] = {
        enabled: true,
        subPermissions,
      };
    });

    setPermissionState(newState);
    setHasChanges(true);
  };

  // Unselect all permissions
  const unselectAll = () => {
    if (!isEditMode) return;

    const newState: PermissionState = {};

    safePermissions.forEach((permission) => {
      const subPermissions: { [key: string]: boolean } = {};
      
      // If permission has sub-permissions, disable them all
      if (permission.subPermissions && permission.subPermissions.length > 0) {
        permission.subPermissions.forEach((subPerm) => {
          subPermissions[subPerm.id] = false;
        });
      }

      newState[permission.id] = {
        enabled: false,
        subPermissions,
      };
    });

    setPermissionState(newState);
    setHasChanges(true);
  };

  // Check if all permissions are selected
  const areAllSelected = () => {
    return safePermissions.every((permission) => {
      const state = permissionState[permission.id];
      if (!state) return false;
      
      // For permissions without sub-permissions, check the enabled flag
      if (!permission.subPermissions || permission.subPermissions.length === 0) {
        return state.enabled;
      }
      
      // For permissions with sub-permissions, check if all sub-permissions are enabled
      return permission.subPermissions.every((subPerm) => state.subPermissions[subPerm.id]);
    });
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header with Search and Collapse/Expand buttons */}
      <div className="flex items-center justify-between gap-4 px-6 pt-4 pb-4 border-b bg-background">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search permissions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSearch}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isEditMode && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={areAllSelected() ? unselectAll : selectAll}
                className="gap-2"
              >
                <Checkbox checked={areAllSelected()} className="pointer-events-none" />
                {areAllSelected() ? 'Unselect All' : 'Select All'}
              </Button>
              {teams.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowCloneModal(true)}
                  className="gap-2"
                >
                  <Copy className="h-4 w-4" />
                  Clone from Team
                </Button>
              )}
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={collapseAll}
            className="gap-2"
          >
            <ChevronsUpDown className="h-4 w-4" />
            Collapse All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={expandAll}
            className="gap-2"
          >
            <ChevronsDownUp className="h-4 w-4" />
            Expand All
          </Button>
        </div>
      </div>

      {/* Scrollable Permission sections */}
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {Object.keys(filteredGroupedPermissions).length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-center">
            <Search className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium text-muted-foreground mb-2">
              No permissions found
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Try adjusting your search query
            </p>
            <Button variant="outline" onClick={clearSearch}>
              Clear Search
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
                {Object.entries(filteredGroupedPermissions).map(([category, categoryPermissions]) => {
                  return (
            <Collapsible
              key={category}
              open={openSections[category] ?? true}
              onOpenChange={() => toggleSection(category)}
            >
              <div className="space-y-3">
                {/* Section Header - Subtle text only */}
                <CollapsibleTrigger className="flex items-center gap-2 w-full group">
                  <div className="flex items-center gap-2 flex-1">
                    {openSections[category] ? (
                      <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform" />
                    ) : (
                      <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform" />
                    )}
                    <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                      {category}
                    </h3>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {categoryPermissions.length} screen{categoryPermissions.length !== 1 ? 's' : ''}
                  </span>
                </CollapsibleTrigger>

                {/* Permission Cards */}
                <CollapsibleContent className="space-y-3">
                  <div className="grid gap-3">
                      {categoryPermissions.map((permission) => {
                        const ScreenIcon = (permission.screenKey && screenIcons[permission.screenKey]) ? screenIcons[permission.screenKey] : Package;
                      const status = getPermissionStatus(permission);
                      const enabledCount = getEnabledCount(permission);

                      return (
                        <Card
                          key={permission.id}
                          className={`border transition-all ${
                            isEditMode ? 'hover:shadow-md cursor-pointer' : ''
                          } ${getCardBorderClass(status)}`}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start gap-3">
                              {/* Screen Icon */}
                              <div
                                className={`flex items-center justify-center w-10 h-10 rounded-md ${getIconContainerClass(status)}`}
                              >
                                <ScreenIcon className="h-5 w-5" />
                              </div>

                              {/* Permission Info */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-sm mb-1">
                                      {permission.displayName}
                                    </h4>
                                    <p className="text-xs text-muted-foreground">
                                      {permission.description}
                                    </p>
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {getStatusBadge(status)}
                                    <Checkbox
                                      checked={status === 'enabled'}
                                      indeterminate={status === 'partial'}
                                      onCheckedChange={() => toggleMainPermission(permission)}
                                      disabled={!isEditMode}
                                      className="ml-2"
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </CardHeader>

                          {/* Sub-permissions */}
                          {permission.subPermissions && permission.subPermissions.length > 0 && (
                            <CardContent className="pt-0">
                              <div className="space-y-3">
                                <div className="flex items-center justify-between text-xs text-muted-foreground">
                                  <span className="font-medium">Permissions</span>
                                  <span className="font-medium">{enabledCount}</span>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                                  {permission.subPermissions.map((subPerm) => (
                                      <SubPermissionCard
                                        key={subPerm.id}
                                        name={subPerm.displayName || subPerm.key || 'Unknown'}
                                        description={subPerm.description ?? ''}
                                        isEnabled={isSubPermissionEnabled(permissionState, permission.id, subPerm.id)}
                                        isEditMode={isEditMode}
                                        onToggle={() => toggleSubPermission(permission.id, subPerm.id)}
                                      />
                                  ))}
                                </div>
                              </div>
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </CollapsibleContent>
              </div>
            </Collapsible>
          );
        })}
        </div>
        )}
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveConfirmation}
        onOpenChange={setShowSaveConfirmation}
        onConfirm={handleConfirmSave}
        title="Save Permission Changes?"
        description="Are you sure you want to save these permission changes? This will update the team's access rights."
        confirmText="Save Changes"
        cancelText="Cancel"
        type="success"
      />

      {/* Unsaved Changes Modal */}
      <AssetUnsavedChangesModal
        open={showUnsavedChangesModal}
        onOpenChange={setShowUnsavedChangesModal}
        onConfirmLeave={handleConfirmLeave}
      />

      {/* Clone Permissions Modal */}
      <ClonePermissionsModal
        open={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        onConfirm={handleClonePermissions}
        currentTeam={team}
        teams={teams}
        permissions={permissions}
        currentPermissions={Object.fromEntries(
          Object.entries(permissionState).map(([permId, state]) => [
            permId,
            Object.entries(state.subPermissions)
              .filter(([, granted]) => granted)
              .map(([subPermId]) => subPermId),
          ])
        )}
      />
    </div>
  );
}
